import type { ReactNode } from "react";
import { ApiOriginProvider } from "@/components/api-origin-provider";
import { FontOptionProvider } from "@/components/font-option-provider";
import { AtlasWebMcpNavigation } from "@/components/portal/atlas-webmcp-navigation";
import { DeploymentVersionWatcher } from "@/components/portal/deployment-version-watcher";
import { PortalChrome } from "@/components/portal/portal-chrome";
import { PortalSessionKeepAlive } from "@/components/portal/portal-session-keep-alive";
import { PortalTelemetry } from "@/components/portal/portal-telemetry";
import { TooltipProvider } from "@/components/ui/tooltip";
import type { PortalRole } from "@/lib/portal-auth-shared";
import type { PortalPageKey, PortalPageState } from "@/lib/portal-pages";

export type PublicPortalRouteShellData = {
  kind: "public";
  deploymentId?: string;
  accessiblePageKeys: PortalPageKey[];
  capacityRiskTrackerEnabled: boolean;
  notesFeedEnabled: boolean;
  pages: PortalPageState[];
};

export type MemberPortalRouteShellData = {
  kind: "member";
  deploymentId?: string;
  advancedAgentEnabled: boolean;
  contentCmsHref?: string;
  currentUser: {
    email: string;
    fullName?: string;
    placeOfWork?: string;
    workRole?: string;
    role: PortalRole;
  };
  featureDecisions: {
    privateVideosEnabled: boolean;
    atlasWebMcpEnabled: boolean;
    capacityRiskTrackerEnabled: boolean;
    notesFeedEnabled: boolean;
    billingManagementEnabled: boolean;
    financialModelDashboardsEnabled: boolean;
  };
  hasPasskey: boolean;
  isDeveloper: boolean;
  showDeveloperAdminItems: boolean;
  memberId?: string;
  pageAccess: {
    accessiblePageKeys: PortalPageKey[];
    pages: PortalPageState[];
  };
  sessionTiming: {
    expiresAt?: number;
    lastSeenAt?: number;
  };
};

export type PortalRouteShellData =
  | PublicPortalRouteShellData
  | MemberPortalRouteShellData;

export function PortalRouteShell({
  children,
  data: shell,
}: {
  children: ReactNode;
  data: PortalRouteShellData;
}) {
  if (shell.kind === "public") {
    return <PublicPortalRouteShell data={shell}>{children}</PublicPortalRouteShell>;
  }

  const portal = (
    <PortalProviders deploymentId={shell.deploymentId}>
      <PortalTelemetry memberId={shell.memberId} role={shell.currentUser.role} />
      <AtlasWebMcpNavigation
        privateVideosEnabled={shell.currentUser.role === "admin" || shell.featureDecisions.privateVideosEnabled}
        accessiblePageKeys={shell.pageAccess.accessiblePageKeys}
        advancedAgentEnabled={shell.advancedAgentEnabled}
        capacityRiskTrackerEnabled={
          shell.currentUser.role === "admin" ||
          shell.featureDecisions.capacityRiskTrackerEnabled
        }
        enabled={shell.featureDecisions.atlasWebMcpEnabled}
        notesFeedEnabled={shell.featureDecisions.notesFeedEnabled}
        pages={shell.pageAccess.pages}
      />
      <PortalSessionKeepAlive
        expiresAt={shell.sessionTiming.expiresAt}
        lastSeenAt={shell.sessionTiming.lastSeenAt}
      />
      <PortalChrome
        advancedAgentEnabled={shell.advancedAgentEnabled}
        billingManagementEnabled={shell.featureDecisions.billingManagementEnabled}
        financialModelPreviewsEnabled={shell.featureDecisions.financialModelDashboardsEnabled}
        contentCmsHref={shell.contentCmsHref}
        currentUser={shell.currentUser}
        hasPasskey={shell.hasPasskey}
        notesFeedEnabled={shell.featureDecisions.notesFeedEnabled}
        capacityRiskTrackerEnabled={
          shell.currentUser.role === "admin" ||
          shell.featureDecisions.capacityRiskTrackerEnabled
        }
        showDeveloperTools={
          shell.isDeveloper && shell.currentUser.role !== "admin"
        }
        showDeveloperAdminItems={shell.showDeveloperAdminItems}
        pages={shell.pageAccess.pages}
        accessiblePageKeys={shell.pageAccess.accessiblePageKeys}
      >
        {children}
      </PortalChrome>
    </PortalProviders>
  );

  return shell.currentUser.role === "admin" ? (
    <FontOptionProvider>{portal}</FontOptionProvider>
  ) : (
    portal
  );
}

export function PublicPortalRouteShell({
  children,
  data: preview,
}: {
  children: ReactNode;
  data: PublicPortalRouteShellData;
}) {
  return (
    <PortalProviders deploymentId={preview.deploymentId}>
      <PortalTelemetry />
      <PortalChrome
        accessiblePageKeys={preview.accessiblePageKeys}
        capacityRiskTrackerEnabled={preview.capacityRiskTrackerEnabled}
        notesFeedEnabled={preview.notesFeedEnabled}
        pages={preview.pages}
        publicPreview
      >
        {children}
      </PortalChrome>
    </PortalProviders>
  );
}

function PortalProviders({
  children,
  deploymentId,
}: {
  children: ReactNode;
  deploymentId?: string;
}) {
  return (
    <TooltipProvider>
      {/* Same-origin today; the former broad Railway API cutover was retired. */}
      <ApiOriginProvider origin="">
        <DeploymentVersionWatcher loadedDeploymentId={deploymentId} />
        {children}
      </ApiOriginProvider>
    </TooltipProvider>
  );
}
